import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Loader2, RefreshCw, UserPlus, StickyNote, ArrowRightCircle, ClipboardList } from 'lucide-react';
import { adminApi, LEAD_STAGES, LEAD_STAGE_LABELS } from '@/components/admin/adminApi';

const STAGE_TONE = {
  new: 'border-slate-600', contacted: 'border-blue-500/50', qualified: 'border-purple-500/50',
  discovery: 'border-indigo-500/50', scope_review: 'border-amber-500/50', proposal: 'border-brand-orange/50',
  won: 'border-green-500/50', onboarding: 'border-brand-blue/50', active_client: 'border-green-400/60',
};

const LeadCard = ({ lead, onStage, onConvert, onNote }) => {
  const app = lead.application || {};
  return (
    <div className={`bg-slate-800 border-l-2 ${STAGE_TONE[lead.stage] || 'border-slate-600'} border border-slate-700 rounded-lg p-3 mb-2`} data-testid={`crm-lead-${lead.id}`}>
      <p className="text-sm font-semibold text-white truncate">{lead.name || 'Unknown'}</p>
      <p className="text-xs text-slate-400 truncate">{lead.email}</p>
      {(app.planInterest || app.objective) && <p className="text-[11px] text-slate-500 mt-1 truncate">{app.planInterest ? `Plan: ${app.planInterest}` : app.objective}</p>}
      <div className="flex items-center gap-1.5 mt-2.5">
        <select value={lead.stage} onChange={(e) => onStage(lead, e.target.value)} className="flex-1 px-2 py-1 rounded bg-slate-900 border border-slate-700 text-[11px] text-white" data-testid={`crm-stage-select-${lead.id}`}>
          {LEAD_STAGES.map((s) => <option key={s} value={s}>{LEAD_STAGE_LABELS[s]}</option>)}
        </select>
        <button onClick={() => onNote(lead)} title="Add note" className="p-1.5 rounded bg-slate-700 text-slate-300 hover:text-white" data-testid={`crm-note-${lead.id}`}><StickyNote className="w-3.5 h-3.5" /></button>
        {lead.converted_project_id ? (
          <span className="p-1.5 rounded bg-green-500/15 text-green-300" title="Converted"><ArrowRightCircle className="w-3.5 h-3.5" /></span>
        ) : (
          <button onClick={() => onConvert(lead)} title="Convert to project" className="p-1.5 rounded bg-brand-blue/20 text-brand-blue hover:bg-brand-blue/30" data-testid={`crm-convert-${lead.id}`}><UserPlus className="w-3.5 h-3.5" /></button>
        )}
      </div>
      {(lead.crm_notes || []).length > 0 && <p className="text-[10px] text-slate-500 mt-1.5">{lead.crm_notes.length} note{lead.crm_notes.length > 1 ? 's' : ''}</p>}
    </div>
  );
};

export const CrmPipelineView = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await adminApi.crmLeads(); setLeads(r.leads || []); }
    catch (e) { toast.error('Could not load CRM leads'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onStage = async (lead, stage) => {
    try { await adminApi.leadStage(lead.id, stage); setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, stage } : l))); toast.success(`${lead.name || lead.email} → ${LEAD_STAGE_LABELS[stage]}`); }
    catch (e) { toast.error('Could not update stage'); }
  };
  const onConvert = async (lead) => {
    if (!window.confirm(`Convert ${lead.name || lead.email} into a DaaS account + project?`)) return;
    try { const r = await adminApi.convertLead(lead.id, {}); toast.success(`Converted → project ${r.project?.id || ''}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Conversion failed'); }
  };
  const onNote = async (lead) => {
    const text = window.prompt(`Add a note for ${lead.name || lead.email}:`);
    if (!text) return;
    try { await adminApi.leadNote(lead.id, text); toast.success('Note added'); load(); }
    catch (e) { toast.error('Could not add note'); }
  };

  const byStage = (s) => leads.filter((l) => (l.stage || 'new') === s);

  return (
    <div data-testid="crm-pipeline-view">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><ClipboardList className="w-5 h-5 text-brand-blue" /> CRM Pipeline</h2>
          <p className="text-xs text-slate-500 mt-0.5">Move leads from first touch to active client · {leads.length} total</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg border border-slate-700 text-slate-400 hover:text-white" data-testid="crm-refresh"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {loading ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 text-brand-blue animate-spin" /></div> : (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="crm-board">
          {LEAD_STAGES.map((stage) => {
            const items = byStage(stage);
            return (
              <div key={stage} className="shrink-0 w-64" data-testid={`crm-column-${stage}`}>
                <div className="flex items-center justify-between px-1 mb-2">
                  <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wide">{LEAD_STAGE_LABELS[stage]}</h3>
                  <span className="text-[11px] font-bold text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full">{items.length}</span>
                </div>
                <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-2 min-h-[120px]">
                  {items.length === 0 ? <p className="text-[11px] text-slate-600 text-center py-6">—</p> : items.map((l) => (
                    <LeadCard key={l.id} lead={l} onStage={onStage} onConvert={onConvert} onNote={onNote} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
